import React, { useEffect, useState, useMemo } from 'react';
import { Trophy, Users, Award, TrendingUp, Edit2, Plus, Minus, Save, X, Search } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import toast from 'react-hot-toast';
import { useAuthContext } from '../contexts/AuthContext';
import {
  getConfig, saveConfig, listUsersWithPoints, adjustUserPoints,
  getStats, GamificationConfig, UserPointsRow, GamificationStats, LevelDef, BadgeDef,
} from '../services/gamification';

type Tab = 'overview' | 'levels' | 'points' | 'users' | 'badges';

export function GamificationPage() {
  const { admin } = useAuthContext();
  const [tab, setTab] = useState<Tab>('overview');
  const [config, setConfig] = useState<GamificationConfig | null>(null);
  const [users, setUsers] = useState<UserPointsRow[]>([]);
  const [stats, setStats] = useState<GamificationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const reload = async () => {
    setLoading(true);
    setError(null);
    let cfg: GamificationConfig | null = null;
    let uList: UserPointsRow[] = [];
    // Try config — gère le cas migration pas appliquée
    try {
      cfg = await getConfig();
    } catch (e: any) {
      setError(`Table gamification_config introuvable. Exécute supabase/migrations/004_gamification_config.sql dans Supabase Dashboard.\n\nDétail : ${e.message || e}`);
      setLoading(false);
      return;
    }
    if (!cfg) {
      setError('Configuration absente. Exécute supabase/migrations/004_gamification_config.sql dans Supabase Dashboard pour créer la table et seed les valeurs par défaut.');
      setLoading(false);
      return;
    }
    setConfig(cfg);
    // Try users (peut échouer si pas d'utilisateurs avec points encore — c'est OK)
    try {
      uList = await listUsersWithPoints();
    } catch (e: any) {
      console.warn('listUsersWithPoints failed:', e);
      // Pas d'erreur fatale — on continue avec liste vide
    }
    setUsers(uList);
    setStats(await getStats(uList));
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q),
    );
  }, [users, search]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>
      <div className="spinner-lg" />
      <div style={{ marginTop: 12, color: '#6B7280', fontSize: 13 }}>Chargement de la config gamification…</div>
    </div>;
  }

  if (error || !config || !stats) {
    return (
      <div style={{ padding: 32, background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 12, color: '#991B1B' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600 }}>⚠️ Configuration gamification indisponible</h3>
        <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, fontFamily: 'inherit', margin: 0 }}>
          {error || 'Erreur inconnue'}
        </pre>
        <div style={{ marginTop: 16, padding: 12, background: '#fff', borderRadius: 8, fontSize: 12, color: '#374151' }}>
          <strong>Action requise :</strong>
          <ol style={{ margin: '8px 0 0 20px', padding: 0 }}>
            <li>Ouvre <a href="https://supabase.com/dashboard/project/ksmtfzkqoavfhvljxfhe/sql/new" target="_blank" rel="noreferrer" style={{ color: '#1B5E20' }}>Supabase SQL Editor</a></li>
            <li>Colle le contenu de <code>supabase/migrations/004_gamification_config.sql</code></li>
            <li>Run → Recharge cette page</li>
          </ol>
        </div>
        <button className="btn-secondary" style={{ marginTop: 16 }} onClick={reload}>Réessayer</button>
      </div>
    );
  }

  return (
    <div>
      {/* Tabs */}
      <div style={tabsStyle}>
        <TabBtn active={tab === 'overview'} onClick={() => setTab('overview')} icon={<TrendingUp size={14} />} label="Vue d'ensemble" />
        <TabBtn active={tab === 'levels'}   onClick={() => setTab('levels')}   icon={<Trophy size={14} />}     label="Niveaux" />
        <TabBtn active={tab === 'points'}   onClick={() => setTab('points')}   icon={<Award size={14} />}      label="Points par action" />
        <TabBtn active={tab === 'users'}    onClick={() => setTab('users')}    icon={<Users size={14} />}      label="Utilisateurs" />
        <TabBtn active={tab === 'badges'}   onClick={() => setTab('badges')}   icon={<Award size={14} />}      label="Badges" />
      </div>

      {tab === 'overview' && <OverviewSection stats={stats} config={config} />}
      {tab === 'levels'   && <LevelsSection config={config} adminEmail={admin?.email || ''} onSaved={reload} />}
      {tab === 'points'   && <PointsSection config={config} adminEmail={admin?.email || ''} onSaved={reload} />}
      {tab === 'users'    && (
        <UsersSection
          users={filteredUsers}
          config={config}
          search={search}
          onSearchChange={setSearch}
          onAdjusted={reload}
        />
      )}
      {tab === 'badges'   && <BadgesSection config={config} stats={stats} adminEmail={admin?.email || ''} onSaved={reload} />}
    </div>
  );
}

// ─────────────────────────────────────────────
// OVERVIEW
// ─────────────────────────────────────────────
function OverviewSection({ stats, config }: { stats: GamificationStats; config: GamificationConfig }) {
  const chartData = config.levels.map(lvl => {
    const found = stats.byLevel.find(b => b.level === lvl.key);
    return { name: `${lvl.emoji} ${lvl.label}`, count: found?.count || 0, color: levelColor(lvl.key) };
  });

  return (
    <div>
      {/* Stats cards */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <StatCard icon={<Users size={16} />} label="Joueurs" value={stats.totalUsers} />
        <StatCard icon={<TrendingUp size={16} />} label="Points distribués" value={stats.totalPoints.toLocaleString('fr-FR')} />
        <StatCard
          icon={<Trophy size={16} />}
          label="Niveau dominant"
          value={
            stats.byLevel.length > 0
              ? config.levels.find(l => l.key === stats.byLevel.sort((a, b) => b.count - a.count)[0].level)?.label || '-'
              : '-'
          }
        />
        <StatCard icon={<Award size={16} />} label="Badges débloqués" value={stats.badgesAwarded.reduce((s, b) => s + b.count, 0)} />
      </div>

      {/* Distribution chart */}
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <h3 style={cardTitleStyle}>Distribution par niveau</h3>
          <span style={cardSubStyle}>Combien d'utilisateurs à chaque palier</span>
        </div>
        <div style={{ height: 280, padding: 16 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#6B7280" />
              <YAxis allowDecimals={false} stroke="#6B7280" />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top 10 */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={cardHeaderStyle}>
          <h3 style={cardTitleStyle}>Top 10 joueurs</h3>
        </div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Pseudo</th>
              <th style={thStyle}>Niveau</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Points</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Check-ins</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Badges</th>
            </tr>
          </thead>
          <tbody>
            {stats.topUsers.map((u, i) => {
              const lvl = config.levels.find(l => l.key === u.level);
              return (
                <tr key={u.user_id} style={trStyle}>
                  <td style={tdStyle}><strong>{i + 1}</strong></td>
                  <td style={tdStyle}>{u.username || u.email?.split('@')[0] || '—'}</td>
                  <td style={tdStyle}>{lvl ? `${lvl.emoji} ${lvl.label}` : u.level}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{u.points}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#6B7280' }}>{u.total_checkins}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{u.badges.length}</td>
                </tr>
              );
            })}
            {stats.topUsers.length === 0 && (
              <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>Aucun utilisateur avec des points pour le moment</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// LEVELS — édition labels, emojis, seuils
// ─────────────────────────────────────────────
function LevelsSection({ config, adminEmail, onSaved }: { config: GamificationConfig; adminEmail: string; onSaved: () => void }) {
  const [draft, setDraft] = useState<LevelDef[]>(config.levels);
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(draft) !== JSON.stringify(config.levels);

  const update = (i: number, patch: Partial<LevelDef>) => {
    setDraft(d => d.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  };
  const removeLevel = (i: number) => setDraft(d => d.filter((_, idx) => idx !== i));
  const addLevel = () => setDraft(d => [...d, { key: `level_${d.length + 1}`, label: 'Nouveau niveau', emoji: '⭐', min_points: 0 }]);

  const onSave = async () => {
    // Validation : pas de doublon de key, ordre par min_points croissant
    const keys = new Set();
    for (const l of draft) {
      if (!l.key.match(/^[a-z0-9_]+$/)) { toast.error(`Clé invalide: "${l.key}" (a-z, 0-9, _ uniquement)`); return; }
      if (keys.has(l.key)) { toast.error(`Clé dupliquée: "${l.key}"`); return; }
      keys.add(l.key);
    }
    setSaving(true);
    try {
      const sorted = [...draft].sort((a, b) => a.min_points - b.min_points);
      await saveConfig({ levels: sorted }, adminEmail);
      toast.success('Niveaux sauvegardés');
      onSaved();
    } catch (e: any) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h3 style={cardTitleStyle}>Configuration des niveaux</h3>
          <span style={cardSubStyle}>Modifie les libellés, emojis et seuils de points. La RPC <code>add_user_points</code> recalcule les niveaux des users existants à chaque attribution.</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={addLevel} disabled={saving}><Plus size={14} /> Ajouter</button>
          <button className="btn-primary" onClick={onSave} disabled={!dirty || saving}>
            <Save size={14} /> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
        </div>
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Emoji</th>
            <th style={thStyle}>Clé interne</th>
            <th style={thStyle}>Libellé affiché</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Seuil min (pts)</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {draft.map((lvl, i) => (
            <tr key={i} style={trStyle}>
              <td style={tdStyle}>
                <input style={inputEmojiStyle} value={lvl.emoji} onChange={e => update(i, { emoji: e.target.value })} maxLength={4} />
              </td>
              <td style={tdStyle}>
                <input style={inputStyle} value={lvl.key} onChange={e => update(i, { key: e.target.value.toLowerCase() })} />
              </td>
              <td style={tdStyle}>
                <input style={inputStyle} value={lvl.label} onChange={e => update(i, { label: e.target.value })} />
              </td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>
                <input
                  type="number"
                  style={{ ...inputStyle, width: 100, textAlign: 'right' }}
                  value={lvl.min_points}
                  onChange={e => update(i, { min_points: parseInt(e.target.value) || 0 })}
                />
              </td>
              <td style={tdStyle}>
                <button className="btn-icon" onClick={() => removeLevel(i)} title="Supprimer"><X size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// POINTS — édition valeur de chaque action
// ─────────────────────────────────────────────
const ACTION_LABELS: Record<string, string> = {
  checkin: 'Check-in dans un bar',
  first_checkin: 'Premier check-in (bonus)',
  match_watched: 'Match regardé',
  correct_prediction: 'Bon pronostic',
  worldcup_match: 'Match Coupe du Monde',
  validate_diffusion: 'Validation diffusion communautaire',
  first_validation: 'Première validation (bonus)',
};

function PointsSection({ config, adminEmail, onSaved }: { config: GamificationConfig; adminEmail: string; onSaved: () => void }) {
  const [draft, setDraft] = useState(config.points);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(config.points);

  const onSave = async () => {
    setSaving(true);
    try {
      await saveConfig({ points: draft }, adminEmail);
      toast.success('Points sauvegardés');
      onSaved();
    } catch (e: any) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h3 style={cardTitleStyle}>Points par action</h3>
          <span style={cardSubStyle}>Combien chaque action rapporte. Appliqué côté app RN au prochain démarrage (lecture de la config).</span>
        </div>
        <button className="btn-primary" onClick={onSave} disabled={!dirty || saving}>
          <Save size={14} /> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </button>
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Action</th>
            <th style={thStyle}>Clé interne</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Points</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(draft).map(([key, value]) => (
            <tr key={key} style={trStyle}>
              <td style={tdStyle}>{ACTION_LABELS[key] || key}</td>
              <td style={tdStyle}><code style={codeStyle}>{key}</code></td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>
                <input
                  type="number"
                  style={{ ...inputStyle, width: 100, textAlign: 'right' }}
                  value={value}
                  onChange={e => setDraft(d => ({ ...d, [key]: parseInt(e.target.value) || 0 }))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// USERS — liste + ajustement points
// ─────────────────────────────────────────────
function UsersSection({
  users, config, search, onSearchChange, onAdjusted,
}: {
  users: UserPointsRow[]; config: GamificationConfig;
  search: string; onSearchChange: (s: string) => void; onAdjusted: () => void;
}) {
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    if (!adjusting) return;
    const d = parseInt(delta);
    if (isNaN(d) || d === 0) { toast.error('Delta invalide'); return; }
    if (!reason.trim()) { toast.error('Précise une raison'); return; }
    setSaving(true);
    try {
      await adjustUserPoints(adjusting, d, reason.trim());
      toast.success(`${d > 0 ? '+' : ''}${d} pts appliqué${Math.abs(d) > 1 ? 's' : ''}`);
      setAdjusting(null); setDelta(''); setReason('');
      onAdjusted();
    } catch (e: any) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h3 style={cardTitleStyle}>{users.length} joueur{users.length > 1 ? 's' : ''}</h3>
          <span style={cardSubStyle}>Triés par points décroissants. Click "Ajuster" pour ajouter ou retirer des points.</span>
        </div>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#9CA3AF' }} />
          <input
            style={{ ...inputStyle, paddingLeft: 30, width: 240 }}
            placeholder="Pseudo ou email…"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Pseudo</th>
            <th style={thStyle}>Email</th>
            <th style={thStyle}>Niveau</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Points</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Check-ins</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Badges</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const lvl = config.levels.find(l => l.key === u.level);
            return (
              <tr key={u.user_id} style={trStyle}>
                <td style={tdStyle}><strong>{u.username || '—'}</strong></td>
                <td style={{ ...tdStyle, color: '#6B7280' }}>{u.email || '—'}</td>
                <td style={tdStyle}>{lvl ? `${lvl.emoji} ${lvl.label}` : u.level}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{u.points}</td>
                <td style={{ ...tdStyle, textAlign: 'right', color: '#6B7280' }}>{u.total_checkins}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{u.badges.length}</td>
                <td style={tdStyle}>
                  <button className="btn-secondary" onClick={() => { setAdjusting(u.user_id); setDelta(''); setReason(''); }}>
                    <Edit2 size={12} /> Ajuster
                  </button>
                </td>
              </tr>
            );
          })}
          {users.length === 0 && (
            <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>Aucun joueur trouvé</td></tr>
          )}
        </tbody>
      </table>

      {/* Modal ajustement */}
      {adjusting && (
        <div style={modalBackdropStyle} onClick={() => setAdjusting(null)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Ajuster les points</h3>
            <p style={{ margin: '0 0 16px', color: '#6B7280', fontSize: 13 }}>
              {users.find(u => u.user_id === adjusting)?.username || users.find(u => u.user_id === adjusting)?.email}
              {' · '}
              <span style={{ fontWeight: 600 }}>{users.find(u => u.user_id === adjusting)?.points} pts actuels</span>
            </p>
            <label style={labelStyle}>Delta (utilise un nombre négatif pour retirer)</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button className="btn-secondary" onClick={() => setDelta(String((parseInt(delta) || 0) - 10))}><Minus size={12} /> 10</button>
              <input type="number" style={{ ...inputStyle, flex: 1, textAlign: 'center', fontSize: 18, fontWeight: 600 }} value={delta} onChange={e => setDelta(e.target.value)} placeholder="0" autoFocus />
              <button className="btn-secondary" onClick={() => setDelta(String((parseInt(delta) || 0) + 10))}><Plus size={12} /> 10</button>
            </div>
            <label style={labelStyle}>Raison (loggée dans l'historique admin)</label>
            <input style={{ ...inputStyle, width: '100%', marginBottom: 16 }} value={reason} onChange={e => setReason(e.target.value)} placeholder="Ex: bug compensation, événement spécial…" />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setAdjusting(null)}>Annuler</button>
              <button className="btn-primary" onClick={apply} disabled={saving}>
                {saving ? 'Application…' : 'Appliquer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// BADGES — édition + stats
// ─────────────────────────────────────────────
function BadgesSection({
  config, stats, adminEmail, onSaved,
}: {
  config: GamificationConfig; stats: GamificationStats; adminEmail: string; onSaved: () => void;
}) {
  const [draft, setDraft] = useState<BadgeDef[]>(config.badges);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(config.badges);

  const update = (i: number, patch: Partial<BadgeDef>) => {
    setDraft(d => d.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  };
  const removeBadge = (i: number) => setDraft(d => d.filter((_, idx) => idx !== i));
  const addBadge = () => setDraft(d => [...d, { key: `badge_${d.length + 1}`, label: 'Nouveau badge', emoji: '🏅', description: '' }]);

  const onSave = async () => {
    const keys = new Set();
    for (const b of draft) {
      if (!b.key.match(/^[a-z0-9_]+$/)) { toast.error(`Clé invalide: "${b.key}"`); return; }
      if (keys.has(b.key)) { toast.error(`Clé dupliquée: "${b.key}"`); return; }
      keys.add(b.key);
    }
    setSaving(true);
    try {
      await saveConfig({ badges: draft }, adminEmail);
      toast.success('Badges sauvegardés');
      onSaved();
    } catch (e: any) {
      toast.error(`Erreur : ${e.message}`);
    } finally { setSaving(false); }
  };

  const countMap = useMemo(() => {
    const m: Record<string, number> = {};
    stats.badgesAwarded.forEach(b => { m[b.badge] = b.count; });
    return m;
  }, [stats.badgesAwarded]);

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h3 style={cardTitleStyle}>Badges</h3>
          <span style={cardSubStyle}>Définition + nombre de joueurs ayant débloqué chaque badge.</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={addBadge} disabled={saving}><Plus size={14} /> Ajouter</button>
          <button className="btn-primary" onClick={onSave} disabled={!dirty || saving}>
            <Save size={14} /> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
        </div>
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Emoji</th>
            <th style={thStyle}>Clé</th>
            <th style={thStyle}>Libellé</th>
            <th style={thStyle}>Description</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Débloqué par</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {draft.map((b, i) => (
            <tr key={i} style={trStyle}>
              <td style={tdStyle}><input style={inputEmojiStyle} value={b.emoji} onChange={e => update(i, { emoji: e.target.value })} maxLength={4} /></td>
              <td style={tdStyle}><input style={inputStyle} value={b.key} onChange={e => update(i, { key: e.target.value.toLowerCase() })} /></td>
              <td style={tdStyle}><input style={inputStyle} value={b.label} onChange={e => update(i, { label: e.target.value })} /></td>
              <td style={tdStyle}><input style={{ ...inputStyle, width: '100%' }} value={b.description} onChange={e => update(i, { description: e.target.value })} /></td>
              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#6B7280' }}>{countMap[b.key] || 0}</td>
              <td style={tdStyle}><button className="btn-icon" onClick={() => removeBadge(i)}><X size={14} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// Helpers UI
// ─────────────────────────────────────────────
function TabBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '8px 14px', borderRadius: 999,
      background: active ? '#1B5E20' : '#fff',
      color: active ? '#fff' : '#374151',
      border: `1px solid ${active ? '#1B5E20' : '#E5E7EB'}`,
      fontSize: 13, fontWeight: 500, cursor: 'pointer',
    }}>
      {icon} {label}
    </button>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: any }) {
  return (
    <div className="stat-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ color: '#1B5E20' }}>{icon}</div>
        <div>
          <div className="stat-label">{label}</div>
          <div className="stat-value" style={{ fontSize: 22 }}>{value}</div>
        </div>
      </div>
    </div>
  );
}

function levelColor(key: string): string {
  const colors: Record<string, string> = {
    remplacant: '#9CA3AF',
    titulaire: '#3B82F6',
    capitaine: '#F59E0B',
    international: '#F97316',
    legende: '#DC2626',
  };
  return colors[key] || '#1B5E20';
}

const tabsStyle: React.CSSProperties = { display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' };
const cardStyle: React.CSSProperties = { background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' };
const cardHeaderStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottom: '1px solid #F3F4F6', gap: 16, flexWrap: 'wrap' };
const cardTitleStyle: React.CSSProperties = { margin: 0, fontSize: 16, fontWeight: 600 };
const cardSubStyle: React.CSSProperties = { fontSize: 12, color: '#6B7280' };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' };
const thStyle: React.CSSProperties = { padding: '10px 16px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', fontWeight: 500, background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' };
const trStyle: React.CSSProperties = { borderBottom: '1px solid #F3F4F6' };
const tdStyle: React.CSSProperties = { padding: '10px 16px', fontSize: 13 };
const inputStyle: React.CSSProperties = { padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' };
const inputEmojiStyle: React.CSSProperties = { ...inputStyle, width: 50, fontSize: 18, textAlign: 'center' };
const codeStyle: React.CSSProperties = { fontFamily: 'monospace', fontSize: 11, color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: 4 };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 };
const modalBackdropStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
const modalStyle: React.CSSProperties = { background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '90vw' };
